import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {DisplayMode} from '@github-ui/issue-create/DisplayMode'
import {getSafeConfig} from '@github-ui/issue-create/getSafeConfig'
import {IssueCreateContextProvider} from '@github-ui/issue-create/IssueCreateContext'
import {TemplateListInternal} from '@github-ui/issue-create/TemplateListPane'
import {handleRepositoryDataError} from '@github-ui/relay-error-helpers'
import {useNavigate} from '@github-ui/use-navigate'
import {useUser} from '@github-ui/use-user'
import {Heading, Link} from '@primer/react'
import {Box} from '@primer/styled-react'
import {type EntryPointComponent, graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {useEntryPointsLoader} from '../../hooks/use-entrypoint-loaders'
import {AnalyticsWrapper} from '../AnalyticsWrapper'
import type {IssueRepoNewChoosePageContentInternal$key} from './__generated__/IssueRepoNewChoosePageContentInternal.graphql'
import type {IssueRepoNewChoosePageQuery} from './__generated__/IssueRepoNewChoosePageQuery.graphql'
import styles from './IssueCreatePane.module.css'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query IssueRepoNewChoosePageQuery($name: String!, $owner: String!) {
    repository(owner: $owner, name: $name) @catch(to: RESULT) {
      id @required(action: THROW)
      ...IssueRepoNewChoosePageContentInternal
    }
  }
`

export const IssueRepoNewChoosePage: EntryPointComponent<
  {
    pageQuery: IssueRepoNewChoosePageQuery
  },
  Record<string, never>
> = ({queries: {pageQuery}}) => {
  const {queryRef} = useEntryPointsLoader(pageQuery, PageQuery)

  if (!queryRef) return null

  return (
    <AnalyticsWrapper category="Repository Issue Create">
      <IssueRepoNewChoosePageContent pageQueryRef={queryRef} />
    </AnalyticsWrapper>
  )
}

export function IssueRepoNewChoosePageContent({
  pageQueryRef,
}: {
  pageQueryRef: PreloadedQuery<IssueRepoNewChoosePageQuery>
}) {
  const pageData = usePreloadedQuery<IssueRepoNewChoosePageQuery>(PageQuery, pageQueryRef)
  const repositoryData = pageData?.repository

  const okResult = handleRepositoryDataError(repositoryData)
  if (!okResult || !okResult.value) {
    return null
  }

  return <IssueRepoNewChoosePageContentInternal repository={okResult.value} />
}

function IssueRepoNewChoosePageContentInternal({repository}: {repository: IssueRepoNewChoosePageContentInternal$key}) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const {currentUser} = useUser()
  const navigate = useNavigate()
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueRepoNewChoosePageContentInternal on Repository {
        ...TemplateListPane
        name
        owner {
          login
        }
      }
    `,
    repository,
  )

  const safeConfig = getSafeConfig({
    defaultDisplayMode: DisplayMode.TemplatePicker,
    insidePortal: false,
    canBypassTemplateSelection: true,
    navigate,
    issueCreateArguments: {
      repository: {
        owner: data.owner.login,
        name: data.name,
      },
    },
  })

  if (!currentUser) {
    return <div>Current user not found</div>
  }
  const {avatarUrl, login} = currentUser

  return (
    <IssueCreateContextProvider optionConfig={safeConfig} preselectedData={undefined}>
      <div className={styles.createPane}>
        <Link href={`/${login}`} className={styles.avatarLink} {...getHovercardAttributesForType('user', {login})}>
          <span className="sr-only">{LABELS.viewProfile(login)}</span>
          <GitHubAvatar src={avatarUrl} size={32} alt={''} className={styles.avatar} />
        </Link>
        <div className={styles.createPaneContainer} data-testid="issue-create-pane-container" data-hpc>
          <Heading as="h1" className={styles.header}>
            {LABELS.issueCreatePaneTitle}
          </Heading>
          <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 2}} tabIndex={-1} data-hpc>
            <TemplateListInternal repository={data} />
          </Box>
        </div>
      </div>
    </IssueCreateContextProvider>
  )
}
